import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useModal } from "../../../../hooks/modalsHooks";
import Typography from "../../../../components/Typography/Typography";
import ModalLayout from "../../../../components/modals/ModalLayout";
import Form from "../../../../components/forms/Form";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Button from "../../../../components/Button/Button";
import RecordForm from "../BatchRecords/RecordForm";
import DependencyContainer from "../BatchRecords/DependencyContainer";
import { useUploadFileMutation } from "../../../../services/fileManagementService";
import {
    convertFormDataForEditing,
    normalizeFileObject,
} from "../../../../utils/aiAssistUtils";

export default function EditAssetModal({
    assetData,
    onAssetUpdated,
    id,
    selectedBatch,
    selectedAssetType,
    assetTypeList,
    allExtractedAssetsDependencies = [],
}) {
    const { hideModal, setModalLoading } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState("");
    const [dependencies, setDependencies] = useState([]);
    const [hasMissingDependencies, setHasMissingDependencies] = useState(false);
    const [groupRepeatCount, setGroupRepeatCount] = useState({});
    const [updatedImages, setUpdatedImages] = useState([]);
    const [fileFieldValues, setFileFieldValues] = useState({});
    const formRef = useRef();
    const [uploadFile] = useUploadFileMutation();

    const setLoadingState = useCallback(
        (loading, step = "") => {
            setIsLoading(loading);
            setModalLoading(loading);
            if (step) setLoadingStep(step);
        },
        [setModalLoading],
    );

    const mapAssetImages = useCallback(
        (images) =>
            Array.isArray(images)
                ? images.map(normalizeFileObject).filter(Boolean)
                : [],
        [],
    );

    const resolveUnit = useCallback((type, value) => {
        const units = type?.unit_category?.units;
        if (!units) return value;
        const match = units.find((u) => u.id?.toString() === value?.toString());
        return match || value;
    }, []);

    const handleFileUpload = useCallback(
        async (file) => {
            if (!file || !(file instanceof File)) {
                return normalizeFileObject(file);
            }

            try {
                const formData = new FormData();
                formData.append("file", file);
                const uploadResponse = await uploadFile({ file: formData });

                if (uploadResponse.error) {
                    throw new Error("File upload failed");
                }

                const uploadedFileData = uploadResponse.data?.data;
                return normalizeFileObject(uploadedFileData);
            } catch (error) {
                throw new Error(`File upload failed: ${error.message}`);
            }
        },
        [uploadFile],
    );

    const handleFormFileSelected = useCallback((fieldName, files) => {
        setFileFieldValues((prev) => ({
            ...prev,
            [fieldName]: files,
        }));
        formRef.current?.setFieldValue(fieldName, files);
    }, []);

    useEffect(() => {
        setUpdatedImages(mapAssetImages(assetData?.asset_images));
    }, [assetData?.asset_images, mapAssetImages]);

    useEffect(() => {
        if (!selectedAssetType?.dependencyConfig?.dependencies) {
            setDependencies([]);
            return;
        }

        const assetDataDeps = Array.isArray(assetData?.dependencies)
            ? assetData.dependencies
            : [];
        const mappedDependencies =
            selectedAssetType.dependencyConfig.dependencies.map((dep) => {
                const selectedAssets = assetDataDeps.map((d) => ({
                    asset_id: d.asset_id || d.assetId || d.asset_uuid,
                    asset_uuid: d.asset_uuid || d.assetId || d.asset_id,
                    quantity: d.qty || d.quantity,
                    unit: d.unit,
                }));

                return {
                    ...dep,
                    selectedAssets:
                        selectedAssets.length > 0 ? selectedAssets : [],
                };
            });

        setDependencies(mappedDependencies);
    }, [selectedAssetType, assetData]);

    const calculateGroupRepeatCounts = useCallback(
        (assetTypeConfig, formData) => {
            if (
                !assetTypeConfig?.formConfig?.fields ||
                !Array.isArray(formData)
            ) {
                return {};
            }

            const groupCounts = {};
            const allGroupIds = [
                ...new Set(
                    assetTypeConfig.formConfig.fields
                        .filter(
                            (field) => field.groupId && field.groupId !== "0",
                        )
                        .map((field) => field.groupId),
                ),
            ];

            allGroupIds.forEach((groupId) => {
                const groupTypeEntry = formData.find(
                    (item) =>
                        item.type === "group" &&
                        item.groupId === groupId &&
                        Array.isArray(item.value),
                );

                if (groupTypeEntry) {
                    groupCounts[groupId] = Math.max(
                        1,
                        groupTypeEntry.value.length,
                    );
                    return;
                }

                const groupFields = formData.filter(
                    (item) => item.groupId === groupId,
                );
                if (groupFields.length > 0) {
                    const fieldsByName = {};
                    groupFields.forEach((item) => {
                        if (!fieldsByName[item.name])
                            fieldsByName[item.name] = [];
                        fieldsByName[item.name].push(item);
                    });

                    const maxCount = Math.max(
                        ...Object.values(fieldsByName).map((arr) => arr.length),
                        1,
                    );
                    groupCounts[groupId] = maxCount;
                } else {
                    groupCounts[groupId] = 1;
                }
            });

            return groupCounts;
        },
        [],
    );

    useEffect(() => {
        if (selectedAssetType?.formConfig?.fields) {
            const counts = calculateGroupRepeatCounts(
                selectedAssetType,
                assetData?.form_data,
            );
            console.log("Group Repeat Count calculated:", counts);
            console.log("Asset Form Data:", assetData?.form_data);
            setGroupRepeatCount(counts);
        }
    }, [selectedAssetType, assetData?.form_data, calculateGroupRepeatCounts]);

    const unitOptions = useMemo(() => {
        const units = selectedAssetType?.unit_category?.units || [];
        return units.map((unit) => ({
            label: `${unit.name} (${unit.symbol})`,
            value: unit.id,
        }));
    }, [selectedAssetType]);

    const isAddRecordButtonDisabled = useMemo(() => {
        if (hasMissingDependencies) return true;
        if (dependencies.length === 0) return false;
        return dependencies.every((dep) => dep.selectedAssets.length === 0);
    }, [hasMissingDependencies, dependencies]);

    const initialValues = useMemo(() => {
        const unitData = assetData?.unit;
        let unitValue = "";

        if (unitData && selectedAssetType?.unit_category?.units) {
            const unitName =
                typeof unitData === "string" ? unitData : unitData.name;
            const unitId =
                typeof unitData === "string" ? unitData : unitData.id;

            const matchingUnit = selectedAssetType.unit_category.units.find(
                (unit) =>
                    unit.name.toLowerCase() === unitName?.toLowerCase() ||
                    unit.symbol.toLowerCase() === unitName?.toLowerCase() ||
                    unit.id.toString() === unitId?.toString(),
            );

            unitValue =
                matchingUnit?.id ||
                selectedAssetType.unit_category.units[0]?.id ||
                "";
        }

        const formDataValues = assetData?.form_data
            ? convertFormDataForEditing(assetData.form_data, selectedAssetType)
            : {};

        const assetImages = mapAssetImages(assetData?.asset_images);

        return {
            asset_id: assetData?.asset_id || assetData?.assetId || "",
            asset_type: selectedAssetType?.id || assetData?.asset_type_id || "",
            unit: unitValue,
            qty: assetData?.qty,
            dependencies: assetData?.dependencies,
            asset_images: assetImages,
            ...formDataValues,
        };
    }, [assetData, selectedAssetType, mapAssetImages]);

    const initializeFileFields = useCallback(() => {
        const newFileFieldValues = {};

        if (!selectedAssetType?.formConfig?.fields) return newFileFieldValues;

        selectedAssetType.formConfig.fields.forEach((field) => {
            if (field.type === "file" || field.type === "image") {
                const fieldName = `field.${field.name}`;
                const fieldValue = initialValues[fieldName];
                if (fieldValue && (fieldValue.id || fieldValue.url)) {
                    newFileFieldValues[fieldName] =
                        normalizeFileObject(fieldValue);
                }

                Object.entries(initialValues).forEach(([key, value]) => {
                    if (
                        key.includes("group") &&
                        key.includes(field.name) &&
                        value &&
                        (value.id || value.url)
                    ) {
                        newFileFieldValues[key] = normalizeFileObject(value);
                    }
                });
            }
        });

        setFileFieldValues(newFileFieldValues);
    }, [selectedAssetType, initialValues]);

    const [formInitialized, setFormInitialized] = useState(false);

    useEffect(() => {
        if (selectedAssetType && initialValues) {
            initializeFileFields();
            setFormInitialized(false);
        }
    }, [selectedAssetType, initialValues, initializeFileFields]);

    useEffect(() => {
        if (formRef.current && initialValues && !formInitialized) {
            Object.entries(initialValues).forEach(([fieldName, value]) => {
                if (value !== undefined && value !== null) {
                    formRef.current.setFieldValue(fieldName, value);

                    if (selectedAssetType?.formConfig?.fields) {
                        const field = selectedAssetType.formConfig.fields.find(
                            (f) =>
                                (fieldName === `field.${f.name}` ||
                                    fieldName.includes(`${f.name}`)) &&
                                (f.type === "file" || f.type === "image"),
                        );

                        if (field && value && (value.id || value.url)) {
                            const normalizedFileValue =
                                normalizeFileObject(value);
                            setFileFieldValues((prev) => ({
                                ...prev,
                                [fieldName]: normalizedFileValue,
                            }));
                        }
                    }
                }
            });
            setFormInitialized(true);
        }
    }, [initialValues, formInitialized, selectedAssetType]);

    const getInitialFileData = useCallback(
        (fieldName) => {
            const fileValue =
                fileFieldValues[fieldName] || initialValues[fieldName];
            return fileValue && (fileValue.id || fileValue.url)
                ? normalizeFileObject(fileValue)
                : null;
        },
        [initialValues, fileFieldValues],
    );

    const handleAddRepeatingGroup = useCallback((groupId) => {
        setGroupRepeatCount((prev) => ({
            ...prev,
            [groupId]: (prev[groupId] || 0) + 1,
        }));
    }, []);

    const handleRemoveRepeatingGroup = useCallback((groupId) => {
        setGroupRepeatCount((prev) => ({
            ...prev,
            [groupId]: Math.max((prev[groupId] || 1) - 1, 1),
        }));
    }, []);

    const handleFileSelected = useCallback((files) => {
        const newFiles = Array.isArray(files) ? files : files ? [files] : [];
        setUpdatedImages(newFiles);
        formRef.current?.setFieldValue("asset_images", newFiles);
    }, []);

    const renderFormField = useCallback(
        (field, fieldName, keyPrefix = "") => {
            const isFileField = field.type === "file" || field.type === "image";
            const initialFileData = isFileField
                ? getInitialFileData(fieldName)
                : null;

            return (
                <RecordForm
                    key={`${keyPrefix}${fieldName}`}
                    fieldType={field.type}
                    name={fieldName}
                    label={field.label}
                    options={field.options || []}
                    isRequired={field.required}
                    multiple={field.multiple || false}
                    initialFile={initialFileData}
                    selectedFile={fileFieldValues[fieldName] || initialFileData}
                    onFileSelected={
                        isFileField
                            ? (files) => {
                                  handleFormFileSelected(fieldName, files);
                              }
                            : handleFileSelected
                    }
                />
            );
        },
        [
            getInitialFileData,
            handleFileSelected,
            fileFieldValues,
            handleFormFileSelected,
        ],
    );

    const renderGroupFields = useCallback(
        (field) => {
            const groupFields = [];
            const repeatCount = groupRepeatCount[field.groupId] || 1;

            for (let repeat = 0; repeat < repeatCount; repeat++) {
                groupFields.push(
                    <div
                        className="field-group-container"
                        key={`group_${field.groupId}_repeat_${repeat}`}
                    >
                        <div className="field-group-header d-flex p-3 pb-0">
                            <div className="text-3">Group {field.groupId}</div>
                            {repeat > 0 && repeat === repeatCount - 1 && (
                                <div
                                    className="text-3 ms-auto field-group-action-remove"
                                    onClick={() =>
                                        handleRemoveRepeatingGroup(
                                            field.groupId,
                                        )
                                    }
                                >
                                    X Remove
                                </div>
                            )}
                        </div>

                        <div className="mx-3 field-group-fields">
                            {selectedAssetType.formConfig.fields
                                .filter(
                                    (groupField) =>
                                        groupField.type !== "group" &&
                                        groupField.groupId === field.groupId,
                                )
                                .map((groupField) => {
                                    const fieldName = `field.group${groupField.groupId}.${repeat}.${groupField.name}`;
                                    return renderFormField(
                                        groupField,
                                        fieldName,
                                        `group_${groupField.groupId}_${repeat}_`,
                                    );
                                })}
                        </div>

                        <div className="field-group-actions">
                            {repeat === repeatCount - 1 && (
                                <div
                                    className="m-3 add-another-node"
                                    onClick={() =>
                                        handleAddRepeatingGroup(field.groupId)
                                    }
                                >
                                    + Add another value set
                                </div>
                            )}
                        </div>
                    </div>,
                );
            }

            return groupFields;
        },
        [
            groupRepeatCount,
            handleRemoveRepeatingGroup,
            handleAddRepeatingGroup,
            selectedAssetType,
            renderFormField,
        ],
    );

    const renderFormFieldsByType = useCallback(
        (field) => {
            if (field.type === "group") {
                return renderGroupFields(field);
            }

            if (field.groupId && field.groupId !== "0") {
                const hasGroupContainer =
                    selectedAssetType.formConfig.fields.some(
                        (f) =>
                            f.type === "group" && f.groupId === field.groupId,
                    );

                if (!hasGroupContainer) {
                    const repeatCount = groupRepeatCount[field.groupId] || 1;
                    const isFirstFieldInGroup =
                        selectedAssetType.formConfig.fields
                            .filter(
                                (f) =>
                                    f.groupId === field.groupId &&
                                    f.type !== "group",
                            )
                            .indexOf(field) === 0;

                    if (isFirstFieldInGroup) {
                        if (repeatCount > 1) {
                            const virtualGroupField = {
                                type: "group",
                                groupId: field.groupId,
                                label: `Group ${field.groupId}`,
                            };
                            return renderGroupFields(virtualGroupField);
                        } else {
                            return selectedAssetType.formConfig.fields
                                .filter(
                                    (f) =>
                                        f.groupId === field.groupId &&
                                        f.type !== "group",
                                )
                                .map((groupField) => {
                                    const fieldName = `field.group${field.groupId}.0.${groupField.name}`;
                                    return renderFormField(
                                        groupField,
                                        fieldName,
                                        `field_group_${field.groupId}_${groupField.name}`,
                                    );
                                });
                        }
                    }
                    return null
                }
                return null;
            }

            const fieldName =
                field.groupId === "0"
                    ? `field.${field.name}`
                    : `field.${field.name}`;
            return renderFormField(field, fieldName, `field_${field.name}`);
        },
        [
            renderGroupFields,
            renderFormField,
            selectedAssetType,
            groupRepeatCount,
        ],
    );

    const shouldRenderField = useCallback((field, allFields) => {
        if (field.type === "group") return true;
        if (!field.groupId || field.groupId === "0") return true;

        const hasGroupContainer = allFields.some(
            (f) => f.type === "group" && f.groupId === field.groupId,
        );

        if (hasGroupContainer) return false;

        const fieldsInGroup = allFields.filter(
            (f) => f.groupId === field.groupId && f.type !== "group",
        );
        return fieldsInGroup.indexOf(field) === 0;
    }, []);

    const handleDependencyChange = useCallback((updatedDependency) => {
        setDependencies((prevDeps) =>
            prevDeps.map((dep) =>
                dep.asset_type_id === updatedDependency.asset_type_id
                    ? updatedDependency
                    : dep,
            ),
        );
    }, []);

    const handleRemoveDependency = useCallback((assetTypeId, index) => {
        setDependencies((prevDeps) =>
            prevDeps.map((dep) =>
                dep.asset_type_id === assetTypeId
                    ? {
                          ...dep,
                          selectedAssets: dep.selectedAssets.filter(
                              (_, i) => i !== index,
                          ),
                      }
                    : dep,
            ),
        );
    }, []);

    const handleMissingDependencies = useCallback((assetTypeId, hasMissing) => {
        setHasMissingDependencies((prev) => prev || hasMissing);
    }, []);

    const processFieldData = useCallback(
        async (fieldKey, fieldValue, assetTypeConfig = null) => {
            const config =
                assetTypeConfig ||
                selectedAssetType.formConfig.fields.find(
                    (field) => field.name === fieldKey,
                );

            if (!config) return null;

            const isFileField =
                config.type === "file" || config.type === "image";

            if (!fieldValue && config.required) {
                throw new Error(`${config.label} is required`);
            }

            if (!fieldValue) return null;

            let processedValue = fieldValue;

            if (isFileField) {
                if (fieldValue instanceof File) {
                    processedValue = await handleFileUpload(fieldValue);
                } else if (
                    fieldValue &&
                    (fieldValue.id || fieldValue.isExisting)
                ) {
                    processedValue = fieldValue;
                }
            }

            return {
                ...config,
                value: processedValue,
                originalFileName:
                    fieldValue instanceof File ? fieldValue.name : undefined,
            };
        },
        [selectedAssetType, handleFileUpload],
    );

    const processGroupData = useCallback(
        async (groupFieldsArray, groupId) => {
            const valueSets = [];

            for (const groupField of groupFieldsArray) {
                const groupFields = [];

                for (const [key, value] of Object.entries(groupField)) {
                    const assetTypeConfig =
                        selectedAssetType.formConfig.fields.find(
                            (config) =>
                                config.name === key &&
                                config.groupId === groupId,
                        );

                    if (assetTypeConfig) {
                        const fieldConfig = await processFieldData(
                            key,
                            value,
                            assetTypeConfig,
                        );
                        if (fieldConfig) groupFields.push(fieldConfig);
                    }
                }

                valueSets.push(groupFields);
            }

            return valueSets;
        },
        [selectedAssetType, processFieldData],
    );

    const processFormData = useCallback(
        async (values) => {
            const formConfig = [];

            for (const [fieldKey, fieldValue] of Object.entries(
                values.field || {},
            )) {
                if (fieldKey.includes("group")) {
                    const groupId = fieldKey.replace("group", "");
                    const valueSets = await processGroupData(
                        fieldValue,
                        groupId,
                    );
                    formConfig.push({
                        groupId,
                        label: "Group",
                        type: "group",
                        value: valueSets,
                    });
                } else {
                    const fieldConfig = await processFieldData(
                        fieldKey,
                        fieldValue,
                    );
                    if (fieldConfig) formConfig.push(fieldConfig);
                }
            }

            return formConfig;
        },
        [processFieldData, processGroupData],
    );

    const processAssetImages = useCallback(async () => {
        if (!Array.isArray(updatedImages)) return [];

        const imageIds = [];
        for (const imageFile of updatedImages) {
            if (imageFile instanceof File) {
                const uploadedId = await handleFileUpload(imageFile);
                if (uploadedId) imageIds.push(uploadedId);
            } else if (imageFile.id) {
                imageIds.push(imageFile);
            }
        }

        return [...new Set(imageIds)];
    }, [updatedImages, handleFileUpload]);

    const handleAssetUpdate = useCallback(
        async (values) => {
            setLoadingState(true, "Saving changes...");

            try {
                const formattedDependencies = dependencies.flatMap(
                    (dep) =>
                        dep.selectedAssets?.map((asset) => ({
                            asset_uuid: asset.asset_uuid,
                            qty: asset.quantity,
                            unit: asset.unit,
                            asset_id: asset.asset_id,
                        })) || [],
                );

                const [formConfig, productImageIds] = await Promise.all([
                    processFormData(values),
                    processAssetImages(),
                ]);

                const updatedAssetData = {
                    ...assetData,
                    asset_id: assetData?.asset_id || assetData?.assetId || "",
                    asset_type_id: values.asset_type,
                    qty: values.qty,
                    unit: resolveUnit(selectedAssetType, values.unit),
                    form_data: formConfig,
                    dependencies: formattedDependencies,
                    asset_images: productImageIds,
                };

                toast.success("Changes saved successfully!");
                hideModal();
                onAssetUpdated?.(updatedAssetData);
            } catch (error) {
                const errorMessage =
                    error.message || "An unexpected error occurred";
                toast.error(
                    errorMessage.includes("File upload failed")
                        ? errorMessage
                        : `Failed to save changes: ${errorMessage}`,
                );
            } finally {
                setLoadingState(false);
            }
        },
        [
            dependencies,
            processFormData,
            processAssetImages,
            assetData,
            selectedAssetType,
            resolveUnit,
            setLoadingState,
            hideModal,
            onAssetUpdated,
        ],
    );

    const validationSchema = Yup.object().shape({
        asset_id: Yup.string().required(
            "The Product ID must not be empty or just spaces",
        ),
        asset_type: Yup.string().required("Asset type is required"),
        qty: Yup.number()
            .required("Quantity is required")
            .min(0, "Quantity must be positive"),
        unit: Yup.string().required("Unit is required"),
    });

    return (
        <ModalLayout className="modal-dialog-centered mx-2" width={600}>
            <div className="card d-flex justify-content-center">
                <ModalLayout.Header>
                    <div className="w-100 p-4 pb-0 d-flex justify-content-center">
                        <Typography variant="h2">
                            Edit Extracted Asset
                        </Typography>
                    </div>
                </ModalLayout.Header>
                <ModalLayout.Body>
                    <div className="w-100 pt-0 p-4 d-flex justify-content-center">
                        <Form
                            initialValues={initialValues}
                            onSubmit={handleAssetUpdate}
                            validationSchema={validationSchema}
                            innerRef={formRef}
                            enableReinitialize={true}
                        >
                            <RecordForm
                                fieldType="text"
                                name="asset_id"
                                label="Product ID"
                                isRequired={true}
                                disabled={true}
                            />

                            <RecordForm
                                fieldType="dropdown"
                                name="asset_type"
                                label="Material type"
                                options={assetTypeList}
                                isRequired={true}
                                disabled={true}
                            />

                            <RecordForm
                                fieldType="dropdown"
                                name="unit"
                                label="Unit"
                                options={unitOptions}
                                disabled={false}
                                isRequired={true}
                            />

                            <RecordForm
                                fieldType="number"
                                name="qty"
                                label="Quantity"
                                isRequired={true}
                                disabled={false}
                            />

                            <RecordForm
                                fieldType="image"
                                name="asset_images"
                                label="Product Images"
                                isRequired={false}
                                multiple={true}
                                disabled={false}
                                initialFile={updatedImages}
                                selectedFile={updatedImages}
                                onFileSelected={handleFileSelected}
                            />

                            {selectedAssetType && (
                                <>
                                    {dependencies.length > 0 && (
                                        <div className="dependencies-container mt-5">
                                            <Typography variant="h2">
                                                Asset Dependencies
                                            </Typography>

                                            {hasMissingDependencies && (
                                                <div className="alert alert-warning mt-2 d-flex align-items-center">
                                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                    <span>
                                                        Asset creation will fail
                                                        because required
                                                        dependencies are not
                                                        available
                                                    </span>
                                                </div>
                                            )}

                                            <div className="mt-4">
                                                {dependencies.map(
                                                    (dep, index) => (
                                                        <DependencyContainer
                                                            key={
                                                                dep.asset_type_id
                                                            }
                                                            index={index}
                                                            dependency={dep}
                                                            unitOptions={
                                                                unitOptions
                                                            }
                                                            onDependencyChange={
                                                                handleDependencyChange
                                                            }
                                                            onRemoveDependency={
                                                                handleRemoveDependency
                                                            }
                                                            projectId={id}
                                                            onMissingDependencies={(
                                                                hasMissing,
                                                            ) =>
                                                                handleMissingDependencies(
                                                                    dep.asset_type_id,
                                                                    hasMissing,
                                                                )
                                                            }
                                                            batch={
                                                                selectedBatch?.value
                                                            }
                                                            allExtractedAssetsDependencies={
                                                                allExtractedAssetsDependencies
                                                            }
                                                        />
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {selectedAssetType.formConfig?.fields
                                        ?.filter((field) =>
                                            shouldRenderField(
                                                field,
                                                selectedAssetType.formConfig
                                                    .fields,
                                            ),
                                        )
                                        ?.map((field) =>
                                            renderFormFieldsByType(field),
                                        )}
                                </>
                            )}

                            <div className="my-4">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    fullWidth={false}
                                    isLoading={isLoading}
                                    loadingText={loadingStep}
                                    disabled={isAddRecordButtonDisabled}
                                >
                                    Make Changes
                                </Button>
                            </div>
                        </Form>
                    </div>
                </ModalLayout.Body>
            </div>
        </ModalLayout>
    );
}
